import { Select } from '@civilgrid/ui/components';
import type { ReactNode } from 'react';
import Checkbox from '~/components2/forms/Checkbox';
import InfoTooltip from '~/components2/InfoTooltip';
import { ExtractFormat, extractFormats, type TExtractFormat, toggleFormat } from './common';

interface FormatPickerProps {
  formats: TExtractFormat[];
  onChange: (formats: TExtractFormat[]) => void;
  /** Whether a submission may carry several formats; when false this is the original single select. */
  multiple: boolean;
}

/** A format's checkbox label, with KMZ's WGS84 explanation attached — plain text for every other format. */
function formatCheckboxLabel(format: (typeof extractFormats)[number]): ReactNode {
  if (format.value !== ExtractFormat.KMZ) return format.label;

  return (
    <span className="inline-flex items-center gap-1">
      {format.label}
      <InfoTooltip
        text="KMZs always export in WGS84."
        label="About KMZ projection"
        testId="extract-download-format-kmz-tooltip"
      />
    </span>
  );
}

/** File-format control for the extract form: a checkbox per format, or a single select when gated off. */
export function FormatPicker({ formats, onChange, multiple }: FormatPickerProps) {
  if (!multiple) {
    return (
      <Select
        aria-label="File Format"
        value={formats[0]}
        onValueChange={(e) => onChange([(e ?? ExtractFormat.DWG) as TExtractFormat])}
        options={extractFormats.map((f) => ({ label: f.label, value: f.value }))}
        triggerClassName="w-fit min-w-[8rem]"
        data-testid="extract-download-format-picker"
      />
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1.5">
        {extractFormats.map((f) => {
          const isSelected = formats.includes(f.value);

          return (
            <Checkbox
              key={f.value}
              label={formatCheckboxLabel(f)}
              checked={isSelected}
              value={isSelected}
              onChange={(checked) => onChange(toggleFormat(formats, f.value, checked))}
              data-testid={`extract-download-format-${f.value}`}
            />
          );
        })}
      </div>

      {/* Clearing every format disables submit; without this the form reads as broken rather than incomplete. */}
      {formats.length === 0 && (
        <div className="pt-1.5 text-2xs text-grey-60" data-testid="extract-download-format-required-hint">
          Select at least one file format.
        </div>
      )}
    </>
  );
}
